import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from './context/AuthContext';
import Login from './components/Login';
import Register from './components/Register';
import { LogOut, Home, Coins } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

interface Landowner {
  name: string;
  email: string;
  parcels: number;
  color: string;
}

interface LandMapData {
  landowners: Omit<Landowner, 'color'>[];
  totalParcels: number;
  totalOwners: number;
}

interface Parcel {
  parcel_id: number;
  position_x: number;
  position_y: number;
  parcel_value: number;
  owner_id: string;
  owner_name: string;
  owner_email: string;
}

interface UserData {
  id: string;
  username: string;
  email: string;
  created_at: string;
  wallet: {
    ahn: number;
    urja: number;
    bhoomi: number;
  };
}

const COLORS = [
  '#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A', '#98D8C8',
  '#F7DC6F', '#BB8FCE', '#85C1E2', '#F8B195', '#C06C84',
  '#6C5B7B', '#F67280', '#00D9FF', '#FF8B94', '#A8E6CF'
];

function App() {
  const { isAuthenticated, user: authUser, logout } = useAuth();
  const [showRegister, setShowRegister] = useState(false);
  const [landMap, setLandMap] = useState<LandMapData | null>(null);
  const [landowners, setLandowners] = useState<Landowner[]>([]);
  const [parcels, setParcels] = useState<Parcel[]>([]);
  const [user, setUser] = useState<UserData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'about' | 'registry' | 'map' | 'myland' | 'admin' | null>(null);
  const [zoomLevel, setZoomLevel] = useState(50); // Pixel size for My Land view - controlled by +/- buttons
  const [scrollPosition, setScrollPosition] = useState({ x: 0, y: 0 }); // Track scroll position for viewport rendering
  const [fullLandSize, setFullLandSize] = useState({ width: 0, height: 0 }); // Full land dimensions for scroll area
  const [selectedParcel, setSelectedParcel] = useState<Parcel | null>(null); // Currently selected parcel for editing
  const [showParcelModal, setShowParcelModal] = useState(false); // Show/hide parcel details modal

  // Admin investment tracking state
  const [initialInvestment, setInitialInvestment] = useState('200');
  const [currentValue, setCurrentValue] = useState('');
  const [profitExtracted, setProfitExtracted] = useState('');
  const [investmentHistory, setInvestmentHistory] = useState<any[]>([]);

  // ETF tracking state
  const [etfValue, setEtfValue] = useState('');
  const [returnAmount, setReturnAmount] = useState('');
  const [returnPercent, setReturnPercent] = useState('');
  const [etfNotes, setEtfNotes] = useState('');
  const [etfRecords, setEtfRecords] = useState<any[]>([]);

  // Fund log state
  const [fundAmount, setFundAmount] = useState('');
  const [fundNotes, setFundNotes] = useState('');
  const [fundLogRecords, setFundLogRecords] = useState<any[]>([]);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const myLandCanvasRef = useRef<HTMLCanvasElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null); // Reference to scrollable container

  useEffect(() => {
    if (isAuthenticated) {
      fetchData();
    }
  }, [isAuthenticated]);

  // Draw map when map tab is active and data is ready
  useEffect(() => {
    if (activeTab === 'map' && parcels.length > 0 && landowners.length > 0 && canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const totalParcels = parcels.length;

      // Calculate grid based on screen aspect ratio (portrait for mobile)
      const screenWidth = window.innerWidth * 0.9; // 90% of screen width
      const screenHeight = window.innerHeight * 0.7; // 70% of screen height
      const aspectRatio = screenHeight / screenWidth;

      // Calculate columns and rows to fill screen proportionally
      const cols = Math.ceil(Math.sqrt(totalParcels / aspectRatio));
      const rows = Math.ceil(totalParcels / cols);

      // Calculate pixel size to fit screen
      const pixelWidth = Math.floor(screenWidth / cols);
      const pixelHeight = Math.floor(screenHeight / rows);
      const pixelSize = Math.min(pixelWidth, pixelHeight, 8); // Max 8px per parcel

      canvas.width = cols * pixelSize;
      canvas.height = rows * pixelSize;

      // White background
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Create color map
      const colorMap: Record<string, string> = {};
      landowners.forEach(owner => {
        colorMap[owner.name] = owner.color;
      });

      // Draw each parcel
      parcels.forEach(parcel => {
        const color = colorMap[parcel.owner_name] || '#CCCCCC';
        ctx.fillStyle = color;
        ctx.fillRect(
          parcel.position_x * pixelSize,
          parcel.position_y * pixelSize,
          pixelSize,
          pixelSize
        );
      });

      // Highlight logged-in user's parcels with gold border
      if (user) {
        ctx.strokeStyle = '#FFD700'; // Gold color
        ctx.lineWidth = 2;
        parcels.forEach(parcel => {
          if (parcel.owner_id === user.id) {
            ctx.strokeRect(
              parcel.position_x * pixelSize,
              parcel.position_y * pixelSize,
              pixelSize,
              pixelSize
            );
          }
        });
      }

      console.log(`✓ Map rendered: ${parcels.length} parcels on ${canvas.width}x${canvas.height} canvas (${cols}x${rows} grid)`);
    }
  }, [activeTab, parcels, landowners, user]);

  // Calculate full land bounds once (memoized to avoid recalculation)
  useEffect(() => {
    if (activeTab === 'myland' && parcels.length > 0 && authUser) {
      const myParcels = parcels.filter(p => p.owner_id === authUser.ahn_id);
      if (myParcels.length === 0) return;

      // Find bounding box of user's parcels - only once
      const xs = myParcels.map(p => p.position_x);
      const ys = myParcels.map(p => p.position_y);
      const minX = Math.min(...xs);
      const maxX = Math.max(...xs);
      const minY = Math.min(...ys);
      const maxY = Math.max(...ys);

      const fullWidth = maxX - minX + 1;
      const fullHeight = maxY - minY + 1;

      setFullLandSize({
        width: fullWidth * zoomLevel,
        height: fullHeight * zoomLevel
      });
    }
  }, [activeTab, parcels, authUser, zoomLevel]);

  // Draw My Land view - only user's parcels, viewport-based rendering
  useEffect(() => {
    if (activeTab !== 'myland' || parcels.length === 0 || !authUser || !myLandCanvasRef.current || !scrollContainerRef.current) {
      return;
    }

    const canvas = myLandCanvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const container = scrollContainerRef.current;
    const containerWidth = container.clientWidth || 400;
    const containerHeight = container.clientHeight || 600;

    // Force minimum viewport size and cap maximum to prevent crashes
    const viewportWidth = Math.min(Math.max(containerWidth, 300), 800);
    const viewportHeight = Math.min(Math.max(containerHeight, 400), 1200);

    const myParcels = parcels.filter(p => p.owner_id === authUser.ahn_id);
    if (myParcels.length === 0) return;

    // Find bounding box
    const xs = myParcels.map(p => p.position_x);
    const ys = myParcels.map(p => p.position_y);
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const minY = Math.min(...ys);
    const maxY = Math.max(...ys);

    const fullWidth = maxX - minX + 1;
    const fullHeight = maxY - minY + 1;

    // Calculate visible grid area with 2x buffer
    const visibleStartX = Math.floor(scrollPosition.x / zoomLevel);
    const visibleStartY = Math.floor(scrollPosition.y / zoomLevel);
    const visibleEndX = Math.ceil((scrollPosition.x + viewportWidth) / zoomLevel);
    const visibleEndY = Math.ceil((scrollPosition.y + viewportHeight) / zoomLevel);

    // Add 2x buffer
    const bufferX = Math.ceil(viewportWidth / zoomLevel);
    const bufferY = Math.ceil(viewportHeight / zoomLevel);

    const renderStartX = Math.max(0, visibleStartX - bufferX);
    const renderEndX = Math.min(fullWidth, visibleEndX + bufferX);
    const renderStartY = Math.max(0, visibleStartY - bufferY);
    const renderEndY = Math.min(fullHeight, visibleEndY + bufferY);

    // Set canvas size to only the render area (not full land size!)
    let renderWidth = renderEndX - renderStartX;
    let renderHeight = renderEndY - renderStartY;

    // CRITICAL SAFETY: Hard cap on render area to prevent crashes (40x40 = 1600 parcels max)
    renderWidth = Math.min(renderWidth, 40);
    renderHeight = Math.min(renderHeight, 40);

    // Adjust renderEnd to match capped width/height
    const actualRenderEndX = renderStartX + renderWidth;
    const actualRenderEndY = renderStartY + renderHeight;

    // CRITICAL: Only create small canvas for viewport
    canvas.width = renderWidth * zoomLevel;
    canvas.height = renderHeight * zoomLevel;

    // Position canvas using CSS to show it at the correct scroll offset
    canvas.style.position = 'absolute';
    canvas.style.left = `${renderStartX * zoomLevel}px`;
    canvas.style.top = `${renderStartY * zoomLevel}px`;

    // White background
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Filter parcels to only those in the render area
    const visibleParcels = myParcels.filter(parcel => {
      const gridX = parcel.position_x - minX;
      const gridY = parcel.position_y - minY;
      return gridX >= renderStartX && gridX < actualRenderEndX &&
             gridY >= renderStartY && gridY < actualRenderEndY;
    });

    console.log(`✓ Rendering ${visibleParcels.length} of ${myParcels.length} parcels (canvas: ${canvas.width}x${canvas.height}px = ${renderWidth}x${renderHeight} parcels at ${renderStartX},${renderStartY})`);

    // Send rendering stats to backend for server-side logging (mobile debugging)
    const token = localStorage.getItem('token');
    if (token) {
      fetch('/bhoomi/api/log-render', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          visibleParcels: visibleParcels.length,
          totalParcels: myParcels.length,
          canvasWidth: canvas.width,
          canvasHeight: canvas.height,
          renderWidth,
          renderHeight,
          renderStartX,
          renderStartY
        })
      }).catch(err => console.error('Failed to log render stats:', err));
    }

    // Render only visible parcels (coordinates relative to render area)
    visibleParcels.forEach(parcel => {
      const globalX = parcel.position_x - minX;
      const globalY = parcel.position_y - minY;

      // Convert to local canvas coordinates
      const x = (globalX - renderStartX) * zoomLevel;
      const y = (globalY - renderStartY) * zoomLevel;

      // Draw parcel background
      ctx.fillStyle = '#10B981';
      ctx.fillRect(x, y, zoomLevel, zoomLevel);

      // Add border
      ctx.strokeStyle = '#059669';
      ctx.lineWidth = 1;
      ctx.strokeRect(x, y, zoomLevel, zoomLevel);

      // Draw parcel number (parcel_id from database)
      if (zoomLevel >= 20) {
        const fontSize = Math.max(7, Math.min(zoomLevel * 0.25, 18));
        ctx.font = `bold ${fontSize}px Arial`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';

        // White text with dark shadow for contrast
        ctx.shadowColor = 'rgba(0, 0, 0, 0.8)';
        ctx.shadowBlur = 2;
        ctx.fillStyle = '#FFFFFF';
        ctx.fillText(parcel.parcel_id.toString(), x + zoomLevel / 2, y + zoomLevel / 2);

        // Reset shadow
        ctx.shadowColor = 'transparent';
        ctx.shadowBlur = 0;
      }
    });
  }, [activeTab, parcels, authUser, zoomLevel, scrollPosition]);

  // Initialize scroll position when My Land tab opens
  useEffect(() => {
    if (activeTab === 'myland' && scrollContainerRef.current) {
      // Reset scroll position when opening tab
      setScrollPosition({ x: 0, y: 0 });
    }
  }, [activeTab]);

  // Handle scroll to track viewport position
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    setScrollPosition({
      x: target.scrollLeft,
      y: target.scrollTop
    });
  };

  // Handle canvas click to select parcel
  const handleCanvasClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!myLandCanvasRef.current || !authUser) return;

    const canvas = myLandCanvasRef.current;
    const rect = canvas.getBoundingClientRect();

    // Get click coordinates relative to canvas
    const clickX = e.clientX - rect.left;
    const clickY = e.clientY - rect.top;

    // Get canvas position from style
    const canvasLeft = parseInt(canvas.style.left || '0');
    const canvasTop = parseInt(canvas.style.top || '0');

    // Calculate global coordinates
    const globalX = canvasLeft + clickX;
    const globalY = canvasTop + clickY;

    // Calculate which parcel was clicked (in grid coordinates)
    const gridX = Math.floor(globalX / zoomLevel);
    const gridY = Math.floor(globalY / zoomLevel);

    // Find bounding box to convert to absolute parcel coordinates
    const myParcels = parcels.filter(p => p.owner_id === authUser.ahn_id);
    if (myParcels.length === 0) return;

    const xs = myParcels.map(p => p.position_x);
    const ys = myParcels.map(p => p.position_y);
    const minX = Math.min(...xs);
    const minY = Math.min(...ys);

    // Find the clicked parcel
    const clickedParcel = parcels.find(p => {
      const parcelGridX = p.position_x - minX;
      const parcelGridY = p.position_y - minY;
      return parcelGridX === gridX && parcelGridY === gridY;
    });

    if (clickedParcel) {
      setSelectedParcel(clickedParcel);
      setShowParcelModal(true);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch landowner summary
      const mapResponse = await fetch('/bhoomi/api/land-map');
      if (!mapResponse.ok) {
        throw new Error(`Failed to fetch land map: ${mapResponse.statusText}`);
      }
      const mapData: LandMapData = await mapResponse.json();
      setLandMap(mapData);

      // Assign colors to landowners
      const ownersWithColors = mapData.landowners.map((owner, index) => ({
        ...owner,
        color: COLORS[index % COLORS.length]
      }));
      setLandowners(ownersWithColors);

      // Fetch all parcels with owner info
      const parcelsResponse = await fetch('/bhoomi/api/parcels-with-owners');
      if (!parcelsResponse.ok) {
        throw new Error(`Failed to fetch parcels: ${parcelsResponse.statusText}`);
      }
      const parcelsData = await parcelsResponse.json();
      setParcels(parcelsData.parcels);

      console.log(`✓ Loaded ${parcelsData.parcels.length} parcels from database`);

      // Fetch current user data (with auth token if available)
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const userResponse = await fetch('/bhoomi/api/me', {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          if (userResponse.ok) {
            const userData = await userResponse.json();
            setUser(userData);
          }
        } catch (userErr) {
          console.log('User not authenticated, showing public view');
        }
      }

    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err instanceof Error ? err.message : 'Unknown error occurred');
    } finally {
      setLoading(false);
    }
  };

  // Fetch investment history (admin only)
  const fetchInvestmentHistory = async () => {
    if (!authUser || authUser.email !== 'ahantravyuh@gmail.com') {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await fetch('/bhoomi/api/admin/investments', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setInvestmentHistory(data.records || []);
        console.log(`✓ Loaded ${data.records?.length || 0} investment records`);
      }
    } catch (error) {
      console.error('Error fetching investment history:', error);
    }
  };

  // Save new investment record (admin only)
  const saveInvestmentRecord = async () => {
    if (!authUser || authUser.email !== 'ahantravyuh@gmail.com') {
      alert('Admin access required');
      return;
    }

    if (!currentValue) {
      alert('Please enter current portfolio value');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        alert('Authentication required');
        return;
      }

      const response = await fetch('/bhoomi/api/admin/investments', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          initialInvestment: parseFloat(initialInvestment),
          currentValue: parseFloat(currentValue),
          profitExtracted: profitExtracted ? parseFloat(profitExtracted) : 0
        })
      });

      if (response.ok) {
        const data = await response.json();
        alert('Investment record saved successfully!');

        // Reset form
        setCurrentValue('');
        setProfitExtracted('');

        // Refresh history
        fetchInvestmentHistory();
      } else {
        const errorData = await response.json();
        alert(`Failed to save: ${errorData.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error saving investment record:', error);
      alert('Failed to save investment record');
    }
  };

  // Fetch ETF tracking records (admin only)
  const fetchEtfRecords = async () => {
    if (!authUser || authUser.email !== 'ahantravyuh@gmail.com') {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await fetch('/bhoomi/api/admin/etf-tracking', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setEtfRecords(data.records || []);
        console.log(`✓ Loaded ${data.records?.length || 0} ETF tracking records`);
      }
    } catch (error) {
      console.error('Error fetching ETF records:', error);
    }
  };

  // Save new ETF tracking record (admin only)
  const saveEtfRecord = async () => {
    if (!authUser || authUser.email !== 'ahantravyuh@gmail.com') {
      alert('Admin access required');
      return;
    }

    if (!etfValue) {
      alert('Please enter ETF value');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        alert('Authentication required');
        return;
      }

      const response = await fetch('/bhoomi/api/admin/etf-tracking', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          etfName: 'iShares MSCI Europe Energy Sector (Acc)',
          etfValue: parseFloat(etfValue),
          returnAmount: returnAmount ? parseFloat(returnAmount) : 0,
          returnPercent: returnPercent ? parseFloat(returnPercent) : 0,
          notes: etfNotes,
          recordedDate: new Date().toISOString().split('T')[0]
        })
      });

      if (response.ok) {
        alert('ETF record saved successfully!');

        // Reset form
        setEtfValue('');
        setReturnAmount('');
        setReturnPercent('');
        setEtfNotes('');

        // Refresh records
        fetchEtfRecords();
      } else {
        const errorData = await response.json();
        alert(`Failed to save: ${errorData.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error saving ETF record:', error);
      alert('Failed to save ETF record');
    }
  };

  // Fetch fund log records (admin only)
  const fetchFundLog = async () => {
    if (!authUser || authUser.email !== 'ahantravyuh@gmail.com') {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await fetch('/bhoomi/api/admin/fund-log', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setFundLogRecords(data.records || []);
        console.log(`✓ Loaded ${data.records?.length || 0} fund log records`);
      }
    } catch (error) {
      console.error('Error fetching fund log records:', error);
    }
  };

  // Save new fund log record (admin only)
  const saveFundLogRecord = async () => {
    if (!authUser || authUser.email !== 'ahantravyuh@gmail.com') {
      alert('Admin access required');
      return;
    }

    if (!fundNotes) {
      alert('Please enter notes');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        alert('Authentication required');
        return;
      }

      const response = await fetch('/bhoomi/api/admin/fund-log', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          amountAhant: fundAmount ? parseFloat(fundAmount) : 0,
          notes: fundNotes
        })
      });

      if (response.ok) {
        alert('Fund log record saved successfully!');

        // Reset form
        setFundAmount('');
        setFundNotes('');

        // Refresh records
        fetchFundLog();
      } else {
        const errorData = await response.json();
        alert(`Failed to save: ${errorData.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error saving fund log record:', error);
      alert('Failed to save fund log record');
    }
  };

  // Delete fund log record (admin only)
  const deleteFundLogRecord = async (recordId: number) => {
    if (!authUser || authUser.email !== 'ahantravyuh@gmail.com') {
      alert('Admin access required');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this fund log record?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        alert('Authentication required');
        return;
      }

      const response = await fetch(`/bhoomi/api/admin/fund-log/${recordId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        alert('Fund log record deleted successfully!');
        // Refresh records
        fetchFundLog();
      } else {
        const errorData = await response.json();
        alert(`Failed to delete: ${errorData.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error deleting fund log record:', error);
      alert('Failed to delete fund log record');
    }
  };

  // Fetch investment history, ETF records, and fund log when admin tab is opened
  useEffect(() => {
    if (activeTab === 'admin' && authUser?.email === 'ahantravyuh@gmail.com') {
      fetchInvestmentHistory();
      fetchEtfRecords();
      fetchFundLog();
    }
  }, [activeTab, authUser]);

  // Show login/register if not authenticated
  if (!isAuthenticated) {
    if (showRegister) {
      return <Register onSwitchToLogin={() => setShowRegister(false)} />;
    }
    return <Login onSwitchToRegister={() => setShowRegister(true)} />;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-900 via-green-700 to-teal-800 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-2xl">
          <div className="animate-pulse flex space-x-4">
            <div className="flex-1 space-y-4 py-1">
              <div className="h-4 bg-green-400 rounded w-3/4"></div>
              <div className="space-y-2">
                <div className="h-4 bg-green-400 rounded"></div>
                <div className="h-4 bg-green-400 rounded w-5/6"></div>
              </div>
            </div>
          </div>
          <p className="mt-4 text-gray-700 text-center font-semibold">Loading land registry...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-900 via-red-700 to-orange-800 flex items-center justify-center p-4">
        <div className="bg-white p-8 rounded-lg shadow-2xl max-w-md">
          <h2 className="text-red-800 text-xl font-bold mb-4">Error Loading Land Map</h2>
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={fetchData}
            className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg transition-colors font-semibold"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!landMap) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-700 to-gray-800 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-2xl">
          <p className="text-gray-700 font-semibold">No land data available</p>
        </div>
      </div>
    );
  }

  const handleHomeClick = () => {
    window.location.href = '/';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-900 via-green-700 to-teal-800">
      {/* Navigation Bar */}
      <nav className="bg-white bg-opacity-10 backdrop-blur-lg border-b border-white border-opacity-30 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <div className="flex items-center space-x-3">
              <Home className="w-8 h-8 text-white" />
              <h1 className="text-2xl font-bold text-white drop-shadow-lg">🏘️ Bhoomi</h1>
            </div>

            {/* Right Side: Balance, User Info, Logout */}
            <div className="flex items-center space-x-2">
              {/* Balance Display */}
              {authUser && (
                <div className="bg-white bg-opacity-20 backdrop-blur-md rounded-xl px-4 py-2 border border-white border-opacity-30 shadow-lg">
                  <div className="flex items-center space-x-2">
                    <Coins className="w-4 h-4 text-green-200" />
                    <p className="text-white text-lg font-bold drop-shadow-lg">
                      {parseFloat(authUser?.bhoomi_balance?.toString() || '0').toFixed(2)}
                    </p>
                    <span className="text-green-100 text-xs font-semibold hidden sm:inline">BHOOMI</span>
                  </div>
                </div>
              )}

              {/* User Profile */}
              {authUser && (
                <div className="bg-white bg-opacity-20 backdrop-blur-md rounded-xl px-3 py-2 border border-white border-opacity-30 shadow-lg hidden md:block">
                  <p className="text-white font-bold text-sm">{authUser?.name}</p>
                  <p className="text-green-100 text-xs opacity-90">{authUser?.email}</p>
                </div>
              )}

              <button
                onClick={handleHomeClick}
                className="px-3 py-2 bg-gradient-to-r from-purple-500/30 to-indigo-500/30 border-2 border-purple-300/50 hover:bg-white hover:bg-opacity-20 text-white rounded-xl transition-all flex items-center space-x-2 font-semibold shadow-lg backdrop-blur-sm"
              >
                <Home className="w-4 h-4" />
                <span className="hidden sm:inline">AhantraVyuh</span>
              </button>

              <button
                onClick={logout}
                className="px-3 py-2 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white rounded-xl transition-all flex items-center space-x-2 font-semibold shadow-lg"
              >
                <LogOut className="w-4 h-4" />
                <span className="hidden sm:inline">Logout</span>
              </button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto p-6">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-2xl p-8 mb-6">
          <h1 className="text-4xl font-bold text-gray-800 mb-2 flex items-center gap-3">
            🏘️ Bhoomi
          </h1>
          <p className="text-gray-600 text-lg">Hybrid Real/Virtual Land Ownership System</p>

          {/* Stats */}
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-gradient-to-r from-green-50 to-teal-50 p-4 rounded-lg border-l-4 border-green-600">
              <p className="text-sm text-gray-600 font-medium">Total Land Parcels</p>
              <p className="text-3xl font-bold text-green-700">{landMap.totalParcels.toLocaleString()}</p>
            </div>
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-lg border-l-4 border-blue-600">
              <p className="text-sm text-gray-600 font-medium">Total Landowners</p>
              <p className="text-3xl font-bold text-blue-700">{landMap.totalOwners}</p>
            </div>
          </div>
        </div>

        {/* Collapsible Sections */}
        <div className="space-y-4 mb-6">
          {/* Bhoomi Map Section - ALL OWNERS */}
          <div className="bg-white rounded-xl shadow-2xl overflow-hidden">
            <button
              onClick={() => setActiveTab(activeTab === 'map' ? null : 'map')}
              className="w-full py-4 px-6 text-left font-semibold transition-colors hover:bg-gray-50 flex items-center justify-between"
            >
              <span className="text-lg text-gray-800">🗺️ Bhoomi Map</span>
              <span className="text-2xl text-gray-400">{activeTab === 'map' ? '−' : '+'}</span>
            </button>
            {activeTab === 'map' && (
              <div className="px-6 pb-6 border-t border-gray-200 pt-6">
                <p className="text-gray-600 mb-4 text-sm">Each colored square represents one land parcel.</p>

                {/* Show user's parcels if logged in */}
                {user && user.wallet.bhoomi > 0 && (
                  <div className="mb-4 p-4 bg-amber-50 border-l-4 border-amber-500 rounded">
                    <h3 className="font-bold text-amber-800 mb-2">
                      ✨ Your Land Parcels ({Math.floor(user.wallet.bhoomi)} parcels)
                    </h3>
                    <p className="text-sm text-amber-700 mb-2">
                      Your parcels are highlighted with gold borders on the map.
                    </p>
                    <details className="text-sm">
                      <summary className="cursor-pointer text-amber-800 font-semibold hover:text-amber-900">
                        View your parcel numbers
                      </summary>
                      <div className="mt-2 p-2 bg-white rounded border border-amber-200 max-h-40 overflow-y-auto">
                        <div className="flex flex-wrap gap-1">
                          {parcels
                            .filter(p => p.owner_id === user.id)
                            .map(p => (
                              <span key={p.parcel_id} className="inline-block px-2 py-1 bg-amber-100 text-amber-800 rounded text-xs font-mono">
                                #{p.parcel_id}
                              </span>
                            ))
                          }
                        </div>
                      </div>
                    </details>
                  </div>
                )}

                <div className="flex justify-center items-center bg-gray-100 p-2 rounded-lg" style={{ minHeight: '75vh' }}>
                  <canvas
                    ref={canvasRef}
                    className="border-2 border-gray-400 rounded shadow-lg"
                    style={{
                      imageRendering: 'pixelated',
                      maxWidth: '100%',
                      maxHeight: '100%'
                    }}
                  />
                </div>
              </div>
            )}
          </div>

          {/* My Land Section - ONLY USER'S PARCELS, ZOOMABLE */}
          {authUser && parseFloat(authUser.bhoomi_balance?.toString() || '0') > 0 && (
            <div className="bg-white rounded-xl shadow-2xl overflow-hidden">
              <button
                onClick={() => setActiveTab(activeTab === 'myland' ? null : 'myland')}
                className="w-full py-4 px-6 text-left font-semibold transition-colors hover:bg-gray-50 flex items-center justify-between"
              >
                <span className="text-lg text-gray-800">🌟 My Land (Zoomable)</span>
                <span className="text-2xl text-gray-400">{activeTab === 'myland' ? '−' : '+'}</span>
              </button>
              {activeTab === 'myland' && (
                <div className="px-6 pb-6 border-t border-gray-200 pt-6">
                  <div className="mb-4 p-4 bg-green-50 border-l-4 border-green-600 rounded">
                    <h3 className="font-bold text-green-800 mb-2">
                      🌟 Your {parcels.filter(p => p.owner_id === authUser.ahn_id).length} Land Parcels
                    </h3>
                    <p className="text-sm text-green-700 mb-3">
                      Each parcel shows its unique ID number from the database. Drag to pan around your land.
                    </p>

                    {/* Zoom Controls */}
                    <div className="flex items-center gap-3 bg-white p-3 rounded border border-green-200">
                      <button
                        onClick={() => setZoomLevel(Math.max(10, zoomLevel - 10))}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg font-bold hover:bg-green-700 transition-colors"
                      >
                        −
                      </button>
                      <div className="flex-1 text-center">
                        <span className="text-sm font-semibold text-green-800">
                          Zoom: {zoomLevel}px
                        </span>
                      </div>
                      <button
                        onClick={() => setZoomLevel(Math.min(80, zoomLevel + 10))}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg font-bold hover:bg-green-700 transition-colors"
                      >
                        +
                      </button>
                    </div>
                  </div>

                  <div
                    ref={scrollContainerRef}
                    onScroll={handleScroll}
                    className="bg-gray-100 p-2 rounded-lg overflow-auto"
                    style={{
                      maxHeight: '70vh',
                      touchAction: 'pan-x pan-y',
                      WebkitOverflowScrolling: 'touch'
                    }}
                  >
                    {/* Spacer div with full land size to enable scrolling */}
                    <div
                      style={{
                        width: `${fullLandSize.width}px`,
                        height: `${fullLandSize.height}px`,
                        position: 'relative',
                        backgroundColor: '#f3f4f6'
                      }}
                    >
                      <canvas
                        ref={myLandCanvasRef}
                        onClick={handleCanvasClick}
                        className="border-2 border-green-500 rounded shadow-lg cursor-pointer"
                        style={{
                          imageRendering: 'pixelated',
                          position: 'absolute'
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Admin Dashboard - Only visible to admin */}
          {authUser && authUser.email === 'ahantravyuh@gmail.com' && (
            <div className="bg-white rounded-xl shadow-2xl overflow-hidden">
              <button
                onClick={() => setActiveTab(activeTab === 'admin' ? null : 'admin')}
                className="w-full py-4 px-6 text-left font-semibold transition-colors hover:bg-gray-50 flex items-center justify-between"
              >
                <span className="text-lg text-gray-800">🔐 Admin Dashboard</span>
                <span className="text-2xl text-gray-400">{activeTab === 'admin' ? '−' : '+'}</span>
              </button>
              {activeTab === 'admin' && (
                <div className="px-6 pb-6 border-t border-gray-200 pt-6">
                  <div className="mb-6">
                    <h3 className="text-xl font-bold text-gray-800 mb-2">Investment Portfolio Tracking</h3>
                    <p className="text-sm text-gray-600">
                      Track the £200 investment backing the Bhoomi land system. 80% of profits go to landowners as gifts/services, 20% to platform consolidation.
                    </p>
                  </div>

                  {/* Investment Input Form */}
                  <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-6 rounded-lg border border-blue-200 mb-6">
                    <h4 className="font-bold text-blue-900 mb-4 text-lg">Record Investment Update</h4>

                    <div className="space-y-4">
                      {/* Initial Investment */}
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Initial Investment (£)
                        </label>
                        <input
                          type="number"
                          value={initialInvestment}
                          onChange={(e) => setInitialInvestment(e.target.value)}
                          className="w-full px-4 py-2 border border-blue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="200"
                          step="0.01"
                        />
                      </div>

                      {/* Current Value */}
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Current Portfolio Value (£)
                        </label>
                        <input
                          type="number"
                          value={currentValue}
                          onChange={(e) => setCurrentValue(e.target.value)}
                          className="w-full px-4 py-2 border border-blue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Enter current value"
                          step="0.01"
                        />
                      </div>

                      {/* Profit Extracted */}
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Profit Extracted (£)
                        </label>
                        <input
                          type="number"
                          value={profitExtracted}
                          onChange={(e) => setProfitExtracted(e.target.value)}
                          className="w-full px-4 py-2 border border-blue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Enter profit extracted"
                          step="0.01"
                        />
                      </div>

                      {/* Calculated Profit Distribution */}
                      {currentValue && parseFloat(currentValue) > parseFloat(initialInvestment) && (
                        <div className="bg-white p-4 rounded-lg border border-blue-300">
                          <p className="text-sm font-semibold text-gray-700 mb-2">Profit Distribution Preview</p>
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-gray-600">Total Profit:</span>
                              <span className="font-bold text-green-700">
                                £{(parseFloat(currentValue) - parseFloat(initialInvestment)).toFixed(2)}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">To Landowners (80%):</span>
                              <span className="font-bold text-blue-700">
                                £{((parseFloat(currentValue) - parseFloat(initialInvestment)) * 0.8).toFixed(2)}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">To Platform (20%):</span>
                              <span className="font-bold text-purple-700">
                                £{((parseFloat(currentValue) - parseFloat(initialInvestment)) * 0.2).toFixed(2)}
                              </span>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Save Button */}
                      <button
                        onClick={saveInvestmentRecord}
                        className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-3 rounded-lg font-bold hover:from-blue-700 hover:to-indigo-700 transition-all shadow-lg"
                      >
                        Save Investment Record
                      </button>
                    </div>
                  </div>

                  {/* Investment Graph */}
                  {investmentHistory.length > 0 && (
                    <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-6 rounded-lg border border-purple-200 mb-6">
                      <h4 className="font-bold text-purple-900 mb-4 text-lg">Investment Performance Chart</h4>
                      <ResponsiveContainer width="100%" height={400}>
                        <LineChart
                          data={investmentHistory.slice().reverse().map((record, idx) => ({
                            date: new Date(record.recorded_at).toLocaleDateString('en-GB', {
                              day: '2-digit',
                              month: 'short'
                            }),
                            recordNumber: idx + 1,
                            initial: parseFloat(record.initial_investment),
                            current: parseFloat(record.current_value),
                            extracted: parseFloat(record.profit_extracted),
                            profit: parseFloat(record.current_value) - parseFloat(record.initial_investment),
                            toLandowners: ((parseFloat(record.current_value) - parseFloat(record.initial_investment)) * 0.8),
                            toPlatform: ((parseFloat(record.current_value) - parseFloat(record.initial_investment)) * 0.2)
                          }))}
                          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                          <XAxis
                            dataKey="date"
                            stroke="#6B7280"
                            style={{ fontSize: '12px' }}
                          />
                          <YAxis
                            stroke="#6B7280"
                            style={{ fontSize: '12px' }}
                            label={{ value: 'Amount (£)', angle: -90, position: 'insideLeft', style: { fontSize: '12px', fill: '#6B7280' } }}
                          />
                          <Tooltip
                            contentStyle={{
                              backgroundColor: 'white',
                              border: '1px solid #E5E7EB',
                              borderRadius: '8px',
                              fontSize: '12px'
                            }}
                            formatter={(value: any) => `£${parseFloat(value).toFixed(2)}`}
                          />
                          <Legend
                            wrapperStyle={{ fontSize: '12px' }}
                            iconType="line"
                          />
                          <Line
                            type="monotone"
                            dataKey="initial"
                            stroke="#9CA3AF"
                            strokeWidth={2}
                            name="Initial Investment"
                            strokeDasharray="5 5"
                          />
                          <Line
                            type="monotone"
                            dataKey="current"
                            stroke="#10B981"
                            strokeWidth={3}
                            name="Current Value"
                          />
                          <Line
                            type="monotone"
                            dataKey="profit"
                            stroke="#3B82F6"
                            strokeWidth={2}
                            name="Total Profit"
                          />
                          <Line
                            type="monotone"
                            dataKey="toLandowners"
                            stroke="#8B5CF6"
                            strokeWidth={2}
                            name="To Landowners (80%)"
                          />
                          <Line
                            type="monotone"
                            dataKey="toPlatform"
                            stroke="#EC4899"
                            strokeWidth={2}
                            name="To Platform (20%)"
                          />
                          <Line
                            type="monotone"
                            dataKey="extracted"
                            stroke="#F59E0B"
                            strokeWidth={2}
                            name="Profit Extracted"
                          />
                        </LineChart>
                      </ResponsiveContainer>

                      {/* Chart Legend Info */}
                      <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-3 text-xs">
                        <div className="bg-white p-2 rounded border border-gray-200">
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-0.5 bg-gray-400" style={{ borderTop: '2px dashed #9CA3AF' }}></div>
                            <span className="text-gray-600">Baseline</span>
                          </div>
                        </div>
                        <div className="bg-white p-2 rounded border border-green-200">
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-0.5 bg-green-600"></div>
                            <span className="text-gray-600">Portfolio Value</span>
                          </div>
                        </div>
                        <div className="bg-white p-2 rounded border border-blue-200">
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-0.5 bg-blue-600"></div>
                            <span className="text-gray-600">Total Profit</span>
                          </div>
                        </div>
                        <div className="bg-white p-2 rounded border border-purple-200">
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-0.5 bg-purple-600"></div>
                            <span className="text-gray-600">Landowners Share</span>
                          </div>
                        </div>
                        <div className="bg-white p-2 rounded border border-pink-200">
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-0.5 bg-pink-600"></div>
                            <span className="text-gray-600">Platform Share</span>
                          </div>
                        </div>
                        <div className="bg-white p-2 rounded border border-orange-200">
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-0.5 bg-orange-600"></div>
                            <span className="text-gray-600">Extracted</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Investment History */}
                  <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
                    <h4 className="font-bold text-gray-800 mb-4 text-lg">Investment History</h4>
                    {investmentHistory.length === 0 ? (
                      <p className="text-gray-500 text-sm text-center py-4">
                        No investment records yet. Add your first record above.
                      </p>
                    ) : (
                      <div className="space-y-3">
                        {investmentHistory.map((record, idx) => (
                          <div key={idx} className="bg-white p-4 rounded-lg border border-gray-200">
                            <div className="flex justify-between items-start mb-2">
                              <span className="text-xs text-gray-500">
                                {new Date(record.recorded_at).toLocaleDateString()}
                              </span>
                              <span className="text-xs font-semibold text-blue-600">
                                Record #{investmentHistory.length - idx}
                              </span>
                            </div>
                            <div className="grid grid-cols-2 gap-3 text-sm">
                              <div>
                                <p className="text-gray-600">Initial:</p>
                                <p className="font-bold text-gray-800">£{record.initial_investment}</p>
                              </div>
                              <div>
                                <p className="text-gray-600">Current:</p>
                                <p className="font-bold text-green-700">£{record.current_value}</p>
                              </div>
                              <div>
                                <p className="text-gray-600">Profit:</p>
                                <p className="font-bold text-blue-700">
                                  £{(parseFloat(record.current_value) - parseFloat(record.initial_investment)).toFixed(2)}
                                </p>
                              </div>
                              <div>
                                <p className="text-gray-600">Extracted:</p>
                                <p className="font-bold text-purple-700">£{record.profit_extracted}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* ETF Tracker */}
                  <div className="mt-8 pt-8 border-t-4 border-orange-300">
                    <div className="mb-6">
                      <h3 className="text-xl font-bold text-gray-800 mb-2">📈 iShares MSCI Europe Energy Sector ETF Tracker</h3>
                      <p className="text-sm text-gray-600">
                        Track your ETF value over time. Baseline: €10.677 | Sold at: €13.984 (6 Mar 2026)
                      </p>
                    </div>

                    {/* Add New Record Form */}
                    <div className="bg-gradient-to-br from-orange-50 to-amber-50 p-6 rounded-lg border border-orange-200 mb-6">
                      <h4 className="font-bold text-orange-900 mb-4 text-lg">Record New Value</h4>

                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            ETF Value (€) *
                          </label>
                          <input
                            type="number"
                            value={etfValue}
                            onChange={(e) => setEtfValue(e.target.value)}
                            className="w-full px-4 py-2 border border-orange-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                            placeholder="13.984"
                            step="0.001"
                          />
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                              Return (€)
                            </label>
                            <input
                              type="number"
                              value={returnAmount}
                              onChange={(e) => setReturnAmount(e.target.value)}
                              className="w-full px-4 py-2 border border-orange-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                              placeholder="3.307"
                              step="0.001"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                              Return (%)
                            </label>
                            <input
                              type="number"
                              value={returnPercent}
                              onChange={(e) => setReturnPercent(e.target.value)}
                              className="w-full px-4 py-2 border border-orange-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                              placeholder="31.09"
                              step="0.01"
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Notes (optional)
                          </label>
                          <textarea
                            value={etfNotes}
                            onChange={(e) => setEtfNotes(e.target.value)}
                            className="w-full px-4 py-2 border border-orange-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                            placeholder="E.g., Market rally, Energy sector growth, etc."
                            rows={2}
                          />
                        </div>

                        <button
                          onClick={saveEtfRecord}
                          className="w-full bg-gradient-to-r from-orange-600 to-amber-600 text-white py-3 rounded-lg font-bold hover:from-orange-700 hover:to-amber-700 transition-all shadow-lg"
                        >
                          Save ETF Record
                        </button>
                      </div>
                    </div>

                    {/* ETF Value Graph */}
                    {etfRecords.length > 0 && (
                      <div className="bg-gradient-to-br from-teal-50 to-cyan-50 p-6 rounded-lg border border-teal-200 mb-6">
                        <h4 className="font-bold text-teal-900 mb-4 text-lg">ETF Value Over Time</h4>
                        <ResponsiveContainer width="100%" height={350}>
                          <LineChart
                            data={etfRecords.slice().reverse().map((record) => ({
                              date: new Date(record.recorded_date).toLocaleDateString('en-GB', {
                                day: '2-digit',
                                month: 'short'
                              }),
                              value: parseFloat(record.etf_value),
                              baseline: 10.677
                            }))}
                            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                            <XAxis
                              dataKey="date"
                              stroke="#6B7280"
                              style={{ fontSize: '12px' }}
                            />
                            <YAxis
                              stroke="#6B7280"
                              style={{ fontSize: '12px' }}
                              domain={[10, 'auto']}
                              label={{ value: 'Value (€)', angle: -90, position: 'insideLeft', style: { fontSize: '12px', fill: '#6B7280' } }}
                            />
                            <Tooltip
                              contentStyle={{
                                backgroundColor: 'white',
                                border: '1px solid #E5E7EB',
                                borderRadius: '8px',
                                fontSize: '12px'
                              }}
                              formatter={(value: any) => `€${parseFloat(value).toFixed(3)}`}
                            />
                            <Legend
                              wrapperStyle={{ fontSize: '12px' }}
                              iconType="line"
                            />
                            <Line
                              type="monotone"
                              dataKey="baseline"
                              stroke="#94A3B8"
                              strokeWidth={2}
                              strokeDasharray="5 5"
                              name="Baseline (€10.677)"
                            />
                            <Line
                              type="monotone"
                              dataKey="value"
                              stroke="#14B8A6"
                              strokeWidth={3}
                              name="ETF Value"
                              dot={{ fill: '#14B8A6', r: 4 }}
                            />
                          </LineChart>
                        </ResponsiveContainer>

                        <div className="mt-4 grid grid-cols-2 gap-3 text-xs">
                          <div className="bg-white p-2 rounded border border-gray-200">
                            <div className="flex items-center gap-2">
                              <div className="w-3 h-0.5 bg-slate-400" style={{ borderTop: '2px dashed #94A3B8' }}></div>
                              <span className="text-gray-600">Baseline (€10.677)</span>
                            </div>
                          </div>
                          <div className="bg-white p-2 rounded border border-teal-200">
                            <div className="flex items-center gap-2">
                              <div className="w-3 h-0.5 bg-teal-600"></div>
                              <span className="text-gray-600">Current Value</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* ETF History Table */}
                    <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
                      <h4 className="font-bold text-gray-800 mb-4 text-lg">ETF Tracking History</h4>
                      {etfRecords.length === 0 ? (
                        <p className="text-gray-500 text-sm text-center py-4">
                          First record loaded (€13.984 on 6 Mar 2026 from baseline €10.677). Add more records to track progress.
                        </p>
                      ) : (
                        <div className="space-y-3">
                          {etfRecords.map((record, idx) => (
                            <div key={idx} className="bg-white p-4 rounded-lg border border-gray-200">
                              <div className="flex justify-between items-start mb-2">
                                <span className="text-xs text-gray-500">
                                  {new Date(record.recorded_date).toLocaleDateString('en-GB', {
                                    day: '2-digit',
                                    month: 'short',
                                    year: 'numeric'
                                  })}
                                </span>
                                <span className="text-xs font-semibold text-teal-600">
                                  Record #{idx + 1}
                                </span>
                              </div>
                              <div className="grid grid-cols-3 gap-3 text-sm mb-2">
                                <div>
                                  <p className="text-gray-600">Value:</p>
                                  <p className="font-bold text-teal-700 text-lg">€{parseFloat(record.etf_value).toFixed(3)}</p>
                                </div>
                                <div>
                                  <p className="text-gray-600">Return:</p>
                                  <p className={`font-bold ${parseFloat(record.return_amount) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                    {parseFloat(record.return_amount) >= 0 ? '+' : ''}€{parseFloat(record.return_amount).toFixed(3)}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-gray-600">Return %:</p>
                                  <p className={`font-bold ${parseFloat(record.return_percent) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                    {parseFloat(record.return_percent) >= 0 ? '+' : ''}{parseFloat(record.return_percent).toFixed(2)}%
                                  </p>
                                </div>
                              </div>
                              {record.notes && (
                                <div className="mt-2 p-2 bg-orange-50 rounded border border-orange-200">
                                  <p className="text-xs text-gray-700">
                                    <strong>Notes:</strong> {record.notes}
                                  </p>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Fund Log */}
                  <div className="mt-8 pt-8 border-t-4 border-gray-300">
                    <div className="mb-6">
                      <h3 className="text-xl font-bold text-gray-800 mb-2">💰 Fund Log</h3>
                      <p className="text-sm text-gray-600">
                        Track fund allocations and notes (not converted to BHOOMI)
                      </p>
                    </div>

                    {/* Add New Record Form */}
                    <div className="bg-gradient-to-br from-gray-50 to-slate-50 p-6 rounded-lg border border-gray-200 mb-6">
                      <h4 className="font-bold text-gray-900 mb-4 text-lg">Add Fund Record</h4>

                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Amount (AHANT)
                          </label>
                          <input
                            type="number"
                            value={fundAmount}
                            onChange={(e) => setFundAmount(e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-500"
                            placeholder="18.051"
                            step="0.001"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Notes *
                          </label>
                          <textarea
                            value={fundNotes}
                            onChange={(e) => setFundNotes(e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-500"
                            placeholder="E.g., 16.41 × 1.1 or 3100 WAX = 2611 LSWAX"
                            rows={2}
                          />
                        </div>

                        <button
                          onClick={saveFundLogRecord}
                          className="w-full bg-gradient-to-r from-gray-600 to-slate-600 text-white py-3 rounded-lg font-bold hover:from-gray-700 hover:to-slate-700 transition-all shadow-lg"
                        >
                          Save Fund Record
                        </button>
                      </div>
                    </div>

                    {/* Fund Log History Table */}
                    <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
                      <h4 className="font-bold text-gray-800 mb-4 text-lg">Fund Log History</h4>
                      {fundLogRecords.length === 0 ? (
                        <p className="text-gray-500 text-sm text-center py-4">
                          No fund records yet. Add your first record above.
                        </p>
                      ) : (
                        <div className="space-y-3">
                          {fundLogRecords.map((record, idx) => (
                            <div key={idx} className="bg-white p-4 rounded-lg border border-gray-200">
                              <div className="flex justify-between items-start mb-2">
                                <span className="text-xs text-gray-500">
                                  {new Date(record.recorded_at).toLocaleDateString('en-GB', {
                                    day: '2-digit',
                                    month: 'short',
                                    year: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </span>
                                <span className="text-xs font-semibold text-gray-600">
                                  Record #{fundLogRecords.length - idx}
                                </span>
                              </div>
                              <div className="mb-2">
                                <p className="text-gray-600 text-sm">Amount:</p>
                                <p className="font-bold text-gray-800 text-lg">
                                  {parseFloat(record.amount_ahant).toFixed(3)} AHANT
                                </p>
                              </div>
                              <div className="p-2 bg-gray-50 rounded border border-gray-200 mb-2">
                                <p className="text-xs text-gray-700">
                                  <strong>Notes:</strong> {record.notes}
                                </p>
                              </div>
                              <button
                                onClick={() => deleteFundLogRecord(record.id)}
                                className="w-full bg-red-500 hover:bg-red-600 text-white py-2 px-4 rounded-lg text-sm font-semibold transition-colors"
                              >
                                🗑️ Delete Record
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Parcel Details Modal */}
        {showParcelModal && selectedParcel && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
            onClick={() => setShowParcelModal(false)}
          >
            <div
              className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-2xl font-bold text-green-700 flex items-center gap-2">
                  🏘️ Parcel #{selectedParcel.parcel_id}
                </h3>
                <button
                  onClick={() => setShowParcelModal(false)}
                  className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
                >
                  ×
                </button>
              </div>

              {/* Parcel Info */}
              <div className="space-y-3 mb-4">
                <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                  <p className="text-sm text-green-600 font-semibold">Owner</p>
                  <p className="text-gray-800 font-bold">{selectedParcel.owner_name}</p>
                  <p className="text-sm text-gray-600">{selectedParcel.owner_email}</p>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-xs text-gray-500 font-semibold">Position X</p>
                    <p className="text-lg font-bold text-gray-800">{selectedParcel.position_x}</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-xs text-gray-500 font-semibold">Position Y</p>
                    <p className="text-lg font-bold text-gray-800">{selectedParcel.position_y}</p>
                  </div>
                </div>

                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-xs text-gray-500 font-semibold">Parcel Value</p>
                  <p className="text-lg font-bold text-gray-800">{selectedParcel.parcel_value}</p>
                </div>
              </div>

              {/* Edit Section - Only for Owner */}
              {authUser && selectedParcel.owner_id === authUser.ahn_id ? (
                <div className="border-t border-gray-200 pt-4">
                  <p className="text-sm font-semibold text-green-700 mb-3">
                    ✏️ Edit Parcel Properties (Coming Soon)
                  </p>
                  <p className="text-xs text-gray-500">
                    You own this parcel. Future versions will allow you to customize it with buildings, colors, and other elements.
                  </p>
                </div>
              ) : (
                <div className="border-t border-gray-200 pt-4">
                  <p className="text-sm text-gray-500 text-center">
                    🔒 This parcel belongs to {selectedParcel.owner_name}
                  </p>
                </div>
              )}

              {/* Close Button */}
              <button
                onClick={() => setShowParcelModal(false)}
                className="w-full mt-4 bg-gradient-to-r from-green-600 to-teal-600 text-white py-3 rounded-xl font-bold hover:from-green-700 hover:to-teal-700 transition-all"
              >
                Close
              </button>
            </div>
          </div>
        )}

        {/* User Balance Footer */}
        {authUser && (
          <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-lg p-3 text-center mb-4">
            <p className="text-white text-xs opacity-75 mb-1">
              Viewing as: {authUser.name}
            </p>
            <p className="text-white text-xs opacity-90">
              🪙 AHN: {authUser.ahant_balance?.toFixed(2) || '0.00'} •
              ⚡ URJA: {authUser.urja_balance?.toFixed(2) || '0.00'} •
              🏘️ BHOOMI: {authUser.bhoomi_balance?.toFixed(2) || '0.00'}
            </p>
          </div>
        )}

        {/* Footer */}
        <div className="bg-green-900 bg-opacity-50 backdrop-blur-sm border border-green-400 rounded-xl p-4 mb-6">
          <p className="text-green-100 font-semibold text-center">
            🌍 First Hybrid Real/Virtual Land Ownership System
          </p>
          <p className="text-green-200 text-sm text-center mt-2">
            Bhoomi land parcels are non-transferable, permanent assets representing hybrid real/virtual land ownership.
          </p>
        </div>

        {/* Bottom Tabs - About & Registry */}
        <div className="flex gap-2 mb-4">
          <button
            onClick={() => setActiveTab(activeTab === 'about' ? null : 'about')}
            className={`flex-1 py-3 px-4 rounded-lg font-semibold transition-all ${
              activeTab === 'about'
                ? 'bg-white text-green-700 shadow-lg'
                : 'bg-white bg-opacity-20 text-white hover:bg-opacity-30'
            }`}
          >
            📜 About Bhoomi
          </button>
          <button
            onClick={() => setActiveTab(activeTab === 'registry' ? null : 'registry')}
            className={`flex-1 py-3 px-4 rounded-lg font-semibold transition-all ${
              activeTab === 'registry'
                ? 'bg-white text-green-700 shadow-lg'
                : 'bg-white bg-opacity-20 text-white hover:bg-opacity-30'
            }`}
          >
            📋 Registry
          </button>
        </div>

        {/* About Bhoomi Content */}
        {activeTab === 'about' && (
          <div className="bg-white rounded-xl shadow-2xl p-6 mb-4">
            <div className="text-gray-700 space-y-3 leading-relaxed">
              <p>
                <strong>Bhoomi</strong> represents a new model of recognizing work and skill contributions.
                Landowners earned <strong>AHANT</strong> — a utility and reward currency — through their time,
                skills, and contributions to the Ahantravyuh platform.
              </p>
              <p>
                <strong className="text-amber-700">AHANT is NOT purchased with money.</strong> It is earned through work.
                When landowners converted their earned AHANT to Bhoomi parcels, they transformed their
                <strong> time investment and skill investment</strong> into permanent, non-transferable land ownership.
              </p>
              <p>
                Landowners may receive benefits, gifts, or prizes as appreciation for their contributions,
                with no commitments or guarantees. Each parcel is numbered and distributed randomly across
                the territory, creating a decentralized ownership pattern.
              </p>
            </div>
          </div>
        )}

        {/* Land Registry Content */}
        {activeTab === 'registry' && (
          <div className="bg-white rounded-xl shadow-2xl p-6 mb-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {landowners.map((owner, index) => (
                <div
                  key={owner.email}
                  className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 hover:border-green-500 transition-colors"
                >
                  <div
                    className="w-8 h-8 rounded flex-shrink-0 shadow-md"
                    style={{ backgroundColor: owner.color }}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-800 truncate">{owner.name}</p>
                    <p className="text-sm font-bold mt-1" style={{ color: owner.color }}>
                      {owner.parcels.toLocaleString()} parcels
                    </p>
                  </div>
                  <div className="text-2xl">#{index + 1}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default App;
